// LivrosRelacionados.js — "Do mesmo autor" e "Da mesma área" no fim da página.
//
// A página de produto terminava no bloco de InsigneCoins e deixava centenas de
// pixels de fundo vazio abaixo — e, pior, um beco sem saída: quem não comprou
// aquele livro não tinha para onde ir. Estas duas faixas transformam o fim da
// página no começo da próxima visita.
//
// Os dados saem do catálogo já público (/ebook), sem endpoint novo: filtramos
// por autor em comum e por categoria em comum, nessa ordem de prioridade.
import React, { useEffect, useState } from "react";
import CardDestaque from "../cardDestaque/CardDestaque";
import Vitrine from "../vitrine/Vitrine";
import RevealStagger from "../../ui/RevealStagger";
import BotaoVerMais from "./BotaoVerMais";

const API_URL = process.env.REACT_APP_API;
const MAXIMO = 5;

// ===== QUANTOS APARECEM NO CELULAR ANTES DO "MOSTRAR MAIS" (17/09/2026) =====
//
// Duas faixas de cinco cartões, em duas colunas, eram seis fileiras de ~420px:
// mais de 2.000px no fim da página, o maior bloco dela no celular. Recolhida,
// cada faixa mostra UMA fileira (dois livros) — o suficiente para dizer "tem
// mais do mesmo autor aqui" —, e o resto está a um toque.
// A partir de `sm` a grade tem três ou cinco colunas e fica inteira.
const NO_CELULAR = 2;

const idsDosAutores = (livro) =>
  (Array.isArray(livro?.autores) ? livro.autores : [])
    .filter(Boolean)
    .map((a) => (typeof a === "object" ? `id:${a.id}` : `nome:${a}`));

const nomesDasCategorias = (livro) =>
  (Array.isArray(livro?.categorias) ? livro.categorias : [])
    .map((c) => (typeof c === "string" ? c : c?.nome))
    .filter(Boolean);

const precoValido = (p) => p && !isNaN(parseFloat(p)) && parseFloat(p) > 0;

// A MOLDURA É A MESMA DA HOME, e isso é o ponto: estas duas faixas fecham a
// página do livro, e até 16/09/2026 elas eram um h3 solto sobre uma grade —
// mais pobres que as vitrines da home, exatamente onde a pessoa já demonstrou
// interesse e está decidindo se continua no site.
//
// `padding=""`: a moldura traz o respiro lateral das seções da home, e aqui
// ela mora DENTRO da página do produto, que já tem o dela. Sem isto, estas
// duas faixas ficariam recuadas em relação a tudo que está acima delas na
// mesma página.
const Faixa = ({ chapeu, titulo, icone, descricao, livros }) => {
  const [aberta, setAberta] = useState(false);
  if (!livros.length) return null;
  const escondidos = Math.max(0, livros.length - NO_CELULAR);
  return (
    <Vitrine
      chapeu={chapeu}
      titulo={titulo}
      icone={icone}
      descricao={descricao}
      padding=""
      className="mb-12"
    >
      <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-3 md:gap-x-4 gap-y-12 orfa-ao-centro">
        {livros.map((livro, i) => (
          <RevealStagger.Item
            key={livro.id}
            className={"h-full " + (!aberta && i >= NO_CELULAR ? "hidden sm:block" : "")}
          >
            <CardDestaque livro={livro} />
          </RevealStagger.Item>
        ))}
      </RevealStagger>
      {escondidos > 0 && (
        // `mt-[2.5rem]`: o botão de carrinho do card vaza 22px para baixo da
        // borda (ver CardDestaque), e com a margem padrão ele encostava nele.
        <BotaoVerMais
          aberto={aberta}
          aoAlternar={() => setAberta((v) => !v)}
          textoFechado={`Mostrar mais ${escondidos} ${escondidos === 1 ? "livro" : "livros"}`}
          margem="mt-[2.5rem]"
          className="sm:hidden"
        />
      )}
    </Vitrine>
  );
};

const LivrosRelacionados = ({ ebook }) => {
  const [mesmoAutor, setMesmoAutor] = useState([]);
  const [mesmaArea, setMesmaArea] = useState([]);

  useEffect(() => {
    if (!ebook?.id) return;
    let ativo = true;

    (async () => {
      try {
        const r = await fetch(API_URL + "ebook");
        if (!r.ok) return;
        const todos = await r.json();
        if (!ativo || !Array.isArray(todos)) return;

        const autoresAtual = new Set(idsDosAutores(ebook));
        const areasAtual = new Set(nomesDasCategorias(ebook));

        const candidatos = todos.filter(
          (l) => String(l.id) !== String(ebook.id) && (precoValido(l.preco) || precoValido(l.preco_impresso))
        );

        const doAutor = candidatos.filter((l) =>
          idsDosAutores(l).some((a) => autoresAtual.has(a))
        );

        // Um livro só aparece uma vez: se já entrou em "do mesmo autor",
        // não se repete logo abaixo em "da mesma área".
        const jaUsados = new Set(doAutor.slice(0, MAXIMO).map((l) => l.id));
        const daArea = candidatos.filter(
          (l) => !jaUsados.has(l.id) && nomesDasCategorias(l).some((c) => areasAtual.has(c))
        );

        setMesmoAutor(doAutor.slice(0, MAXIMO));
        setMesmaArea(daArea.slice(0, MAXIMO));
      } catch {
        /* sem relacionados → as faixas não aparecem */
      }
    })();

    return () => { ativo = false; };
  }, [ebook?.id, ebook]);

  if (!mesmoAutor.length && !mesmaArea.length) return null;

  const nomeAutor = (() => {
    const a = (Array.isArray(ebook?.autores) ? ebook.autores : []).find(Boolean);
    return a ? (typeof a === "object" ? a.nome : a) : null;
  })();

  return (
    <>
      <Faixa
        chapeu="Mesma autoria"
        titulo={nomeAutor ? `Também de ${nomeAutor}` : "Do mesmo autor"}
        icone="bi-person-lines-fill"
        descricao="Outros títulos assinados por quem escreveu este livro."
        livros={mesmoAutor}
      />
      <Faixa
        chapeu="Mesmo assunto"
        titulo="Da mesma área"
        icone="bi-bookmarks"
        descricao="Livros que dividem alguma das áreas deste título."
        livros={mesmaArea}
      />
    </>
  );
};

export default LivrosRelacionados;
